import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiMail, FiLock, FiAlertCircle, FiCheck, FiUserPlus } = FiIcons;

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Check for guest assessment results that need to be saved
  useEffect(() => {
    const savedGuestAssessment = localStorage.getItem('savedGuestAssessment');
    if (savedGuestAssessment) {
      const data = JSON.parse(savedGuestAssessment);
      if (data.type === 'guest_saved') {
        // Show a message that results will be saved after login
        console.log('Guest assessment detected - will save after login');
      }
    }
  }, []);

  // Initialize default users on component mount
  useEffect(() => {
    initializeDefaultUsers();
  }, []);

  const initializeDefaultUsers = () => {
    const existingUsers = localStorage.getItem('users');
    if (!existingUsers) {
      const defaultUsers = [
        {
          id: 1,
          name: 'Admin User',
          email: 'admin@psychsafety.com',
          password: 'admin123',
          role: 'admin',
          company: 'PsychSafety Pro',
          createdAt: new Date().toISOString(),
          isActive: true,
          lastLogin: null
        },
        {
          id: 2,
          name: 'John Doe',
          email: 'john@example.com',
          password: 'password',
          role: 'user',
          company: 'Tech Corp',
          createdAt: new Date().toISOString(),
          isActive: true,
          lastLogin: null
        },
        {
          id: 3,
          name: 'Jane Smith',
          email: 'jane@example.com',
          password: 'password',
          role: 'user',
          company: 'Digital Solutions',
          createdAt: new Date().toISOString(),
          isActive: true,
          lastLogin: null
        },
      ];
      localStorage.setItem('users', JSON.stringify(defaultUsers));
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const authenticateUser = (email, password) => {
    try {
      const users = JSON.parse(localStorage.getItem('users') || '[]');
      return users.find((u) => u.email === email && u.password === password);
    } catch (error) {
      console.error('Error parsing users:', error);
      return null;
    }
  };

  const updateLastLogin = (userId) => {
    try {
      const users = JSON.parse(localStorage.getItem('users') || '[]');
      const updatedUsers = users.map((user) =>
        user.id === userId
          ? { ...user, lastLogin: new Date().toISOString() }
          : user
      );
      localStorage.setItem('users', JSON.stringify(updatedUsers));
    } catch (error) {
      console.error('Error updating last login:', error);
    }
  };

  const setAuthentication = (user) => {
    const userData = {
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role,
      company: user.company
    };
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const migrateGuestAssessment = (user) => {
    try {
      const savedGuestAssessment = localStorage.getItem('savedGuestAssessment');
      if (savedGuestAssessment) {
        const data = JSON.parse(savedGuestAssessment);
        
        // Create a new assessment record for the authenticated user
        const userAssessment = {
          ...data,
          userId: user.id,
          migratedAt: new Date().toISOString(),
          originalGuestData: true
        };
        
        // Save as the most recent assessment for the user
        localStorage.setItem('assessmentResults', JSON.stringify(userAssessment));
        
        // Clear the guest-specific data
        localStorage.removeItem('savedGuestAssessment');
        localStorage.removeItem('guestAssessment');
        
        console.log('Guest assessment migrated to user account');
        return true;
      }
    } catch (error) {
      console.error('Error migrating guest assessment:', error);
    }
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate form
    if (!formData.email.trim() || !formData.password.trim()) {
      setError('Please enter both email and password');
      setLoading(false);
      return;
    }

    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    try {
      // Authenticate user
      const user = authenticateUser(formData.email, formData.password);

      if (user) {
        if (!user.isActive) {
          setError('Your account has been deactivated. Please contact an administrator.');
        } else {
          // Update last login
          updateLastLogin(user.id);
          
          // Set authentication
          setAuthentication(user);

          // Check for and migrate guest assessment
          const hasGuestAssessment = migrateGuestAssessment(user);

          // Show success message briefly
          setSuccess(true);

          // Navigate after success
          setTimeout(() => {
            const redirectPath = user.role === 'admin' ? '/admin' : (hasGuestAssessment ? '/results' : '/dashboard');
            navigate(redirectPath, { replace: true });
          }, 800);
        }
      } else {
        setError('Invalid credentials. Please check your email and password.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <SafeIcon icon={FiCheck} className="h-16 w-16 text-green-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
            Login Successful!
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Redirecting to your dashboard...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <SafeIcon icon={FiShield} className="h-12 w-12 text-blue-600" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Sign in to PsychSafety Pro
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Manage your team assessments and track psychological safety progress
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                  Remember me
                </label>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">New to PsychSafety Pro?</span>
              </div>
            </div>

            <div className="mt-6 text-center">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <SafeIcon icon={FiUserPlus} className="h-4 w-4 inline mr-1" />
                  Register to unlock the ability to save your team's progress and performance over time.
                </p>
              </div>
              <a
                href="/register"
                className="font-medium text-blue-600 hover:text-blue-500"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/register');
                }}
              >
                Don't have an account? Register here
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;